import React, { useState, useMemo, useEffect } from 'react';
import {
	Modal,
	InputNumber,
	Select,
	Table,
	Typography,
	Space,
	Button,
	message,
	Card,
	Popconfirm,
	Tooltip,
} from 'antd';
import { DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import AssociatedJobField from '../MaterialInventory/AssociatedJobField';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import { useGetInventoryLocationsQuery } from '../../redux/api/material';
import MaterialLocationsSummaryModal from '../Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import {
	toNum,
	getAvailableColumnLabel,
	hasNoAvailability,
	barFitsInLocation,
	getMaterialInfoFromRecord,
	getDimensionCellValue,
	renderLocationOption,
	filterLocationOption,
	NO_LOCATION_VALUE,
	NO_LOCATION_LABEL,
} from './materialLengthLocationUtils';
import { locationSearchText, useLocationLabel } from '../../utils/locationLabel';
import BuildingTag from '../BuildingTag';
import OperatorPinModal from '../Operators/OperatorPinModal';

interface ModifyLengthsModalProps {
	open: boolean;
	onClose: () => void;
	selectedLengths: any[];
	isTablet?: boolean;
	onConfirm?: (
		operatorId: string | number,
		modifications: Array<{
			material_length_id: string;
			attribute_cost?: string;
			new_length?: number;
			difference?: number;
			associated_job_id?: string | null;
			inventory_location_id?: string | null;
		}>
	) => void | Promise<void>;
	/** When provided, each row gets a "Remove from list" button so users can drop accidentally-selected rows without leaving the modal. */
	onRemoveLength?: (id: string) => void;
}

const ATTRIBUTE_COST = {
	ERROR_CORRECTION: 'error_correction',
	FASTLANE_JOB_RELATED: 'fastlane_job_related',
	SWISS_JOB_RELATED: 'swiss_job_related',
	FASTLANE_PROGRAM_RELATED: 'fastlane_program_related',
	SWISS_PROGRAM_RELATED: 'swiss_program_related',
	SCRAP_METAL: 'scrap_metal',
	THROWN_OUT: 'thrown_out',
	MISSING_ERROR: 'missing_error',
} as const;

type AttributeCostValue = (typeof ATTRIBUTE_COST)[keyof typeof ATTRIBUTE_COST];

type AttributeCostOption = { label: string; value: AttributeCostValue };

// Attribute Cost options when Difference is negative (increase in length)
const ATTRIBUTE_COST_OPTIONS_INCREASE: AttributeCostOption[] = [
	{ label: 'Error Correction', value: ATTRIBUTE_COST.ERROR_CORRECTION },
	{ label: 'Fastlane Job Related', value: ATTRIBUTE_COST.FASTLANE_JOB_RELATED },
	{ label: 'Swiss Job Related', value: ATTRIBUTE_COST.SWISS_JOB_RELATED },
];

// Attribute Cost options when Difference is positive (reduction in length)
const ATTRIBUTE_COST_OPTIONS_REDUCTION: AttributeCostOption[] = [
	{ label: 'Fastlane Program Related', value: ATTRIBUTE_COST.FASTLANE_PROGRAM_RELATED },
	{ label: 'Fastlane Job Related', value: ATTRIBUTE_COST.FASTLANE_JOB_RELATED },
	{ label: 'Swiss Program Related', value: ATTRIBUTE_COST.SWISS_PROGRAM_RELATED },
	{ label: 'Swiss Job Related', value: ATTRIBUTE_COST.SWISS_JOB_RELATED },
	{ label: 'Scrap Metal', value: ATTRIBUTE_COST.SCRAP_METAL },
	{ label: 'Thrown Out', value: ATTRIBUTE_COST.THROWN_OUT },
	{ label: 'Missing/Error', value: ATTRIBUTE_COST.MISSING_ERROR },
];

const ModifyLengthsModal: React.FC<ModifyLengthsModalProps> = ({
	open,
	onClose,
	selectedLengths,
	isTablet = false,
	onConfirm,
	onRemoveLength,
}) => {
	const buildingName = useMemo(() => {
		const first = selectedLengths[0];
		return first?.inventory_location?.shelf?.rack?.building?.name ?? null;
	}, [selectedLengths]);
	const formatLocation = useLocationLabel();
	const locLabel = (loc: any, fallback: string) =>
		formatLocation(loc?.display_name) || loc?.code || fallback;
	const [newLengthByKey, setNewLengthByKey] = useState<Record<string, number | undefined>>({});
	// Debounced new lengths used only for location API (avoids a request on every keystroke)
	const [debouncedNewLengthByKey, setDebouncedNewLengthByKey] = useState<
		Record<string, number | undefined>
	>({});
	const [attributeCostByKey, setAttributeCostByKey] = useState<
		Record<string, string | undefined>
	>({});
	const [associatedJobByKey, setAssociatedJobByKey] = useState<
		Record<string, Lineitem | undefined>
	>({});
	// Per-length selected location (candidate locations depend on new length when set)
	const [locationByLengthId, setLocationByLengthId] = useState<Record<string, string>>({});

	// Scenario 16: Material Location Summary modal after Confirm (PTB-4699)
	const [showLocationSummary, setShowLocationSummary] = useState(false);
	/** After Confirm on form fields, ask for PIN before saving. */
	const [operatorPinOpen, setOperatorPinOpen] = useState(false);
	const [showErrors, setShowErrors] = useState(false);
	const [locationSummaryData, setLocationSummaryData] = useState<{
		materialInfo: { type: string; width?: number; height?: number; shape?: string; outer_diameter?: number; inner_diameter?: number; wall_thickness?: number };
		lengths: Array<{ id: string; friendlyId: string; location: string; length: number; isScrapped?: boolean }>;
	}>({ materialInfo: { type: 'N/A' }, lengths: [] });
	const [isSubmitting, setIsSubmitting] = useState(false);
	const DEBOUNCE_MS = 400;

	// Debounce new length values so location API is not called on every keystroke
	useEffect(() => {
		const t = setTimeout(() => {
			setDebouncedNewLengthByKey(newLengthByKey);
		}, DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [newLengthByKey]);

	// Candidate locations: only for rows that have a new length (dropdown disabled until then).
	// Single request, only IDs with new length + length_overrides; no base request.
	const idsWithNewLength = useMemo(() => {
		const ids: string[] = [];
		selectedLengths.forEach((record: any) => {
			const key = record.id?.toString() ?? record.friendly_id;
			const newLen = debouncedNewLengthByKey[key];
			if (newLen != null && Number.isFinite(Number(newLen))) {
				ids.push(String(record.id));
			}
		});
		return ids;
	}, [selectedLengths, debouncedNewLengthByKey]);
	const lengthOverridesParam = useMemo(() => {
		const overrides: Record<string, number> = {};
		selectedLengths.forEach((record: any) => {
			const key = record.id?.toString() ?? record.friendly_id;
			const newLen = debouncedNewLengthByKey[key];
			if (newLen != null && Number.isFinite(Number(newLen))) {
				overrides[String(record.id)] = Number(newLen);
			}
		});
		return Object.keys(overrides).length > 0 ? JSON.stringify(overrides) : undefined;
	}, [selectedLengths, debouncedNewLengthByKey]);

	const buildingId = useMemo(() => {
		const first = selectedLengths[0];
		return first?.inventory_location?.shelf?.rack?.building?.id ?? undefined;
	}, [selectedLengths]);

	const { data: locationsData, isLoading: locationsLoading, isFetching: locationsFetching } =
		useGetInventoryLocationsQuery(
			{
				material_length_ids: idsWithNewLength,
				...(lengthOverridesParam ? { length_overrides: lengthOverridesParam } : {}),
				...(buildingId && { building: buildingId }),
			},
			{
				skip: !open || idsWithNewLength.length === 0,
				refetchOnMountOrArgChange: true,
			}
		);
	const locationsRequesting = locationsLoading || locationsFetching;

	// True when this row has a new length that differs from current (enables location dropdown and attribute cost)
	const rowHasNewLength = (record: any) => {
		const key = record.id?.toString() ?? record.friendly_id;
		const newLen = debouncedNewLengthByKey[key];
		const current = Number(record.current_length);
		return (
			newLen != null &&
			Number.isFinite(Number(newLen)) &&
			Number(newLen) !== (Number.isFinite(current) ? current : 0)
		);
	};

	// True when this row is fully consumed (New Length = 0): the bar no longer occupies a
	// physical location, so the Location dropdown offers "No Location Assigned" instead of candidates.
	const rowIsConsumed = (record: any) => {
		const key = record.id?.toString() ?? record.friendly_id;
		const newLen = debouncedNewLengthByKey[key];
		return newLen != null && Number.isFinite(Number(newLen)) && Number(newLen) === 0;
	};

	// Per–material-length consumption from API (weight, volume, width, height per bar)
	const consumptionByLengthId = useMemo(() => {
		const out: Record<string, { weight_per_bar: number; volume_per_bar: number; width_per_bar: number; height_per_bar: number }> = {};
		(locationsData?.by_material_length ?? []).forEach((entry: any) => {
			const mid = entry.material_length_id != null ? String(entry.material_length_id) : '';
			const c = entry.consumption;
			if (!c) {
				out[mid] = { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
				return;
			}
			out[mid] = {
				weight_per_bar: toNum(c.weight_per_bar),
				volume_per_bar: toNum(c.volume_per_bar),
				width_per_bar: toNum(c.width_per_bar),
				height_per_bar: toNum(c.height_per_bar),
			};
		});
		return out;
	}, [locationsData?.by_material_length]);

	// Valid location IDs per length from API (for initial selection effect)
	const initialValidOptionIdsByLengthId = useMemo(() => {
		const out: Record<string, Set<string>> = {};
		const byMaterialLength = locationsData?.by_material_length ?? [];
		byMaterialLength.forEach((entry: any) => {
			const mid = entry.material_length_id != null ? String(entry.material_length_id) : '';
			const locs = entry.locations ?? [];
			const set = new Set<string>();
			locs.forEach((loc: any) => {
				if (loc) set.add(String(loc.id));
			});
			const len = selectedLengths.find((l: any) => String(l.id) === mid);
			if (len?.inventory_location != null && typeof len.inventory_location === 'object') {
				const loc = len.inventory_location;
				const currentId = loc.id ?? loc;
				if (currentId) set.add(String(currentId));
			}
			out[mid] = set;
		});
		selectedLengths.forEach((len: any) => {
			const mid = String(len.id);
			if (!out[mid]) out[mid] = new Set();
		});
		return out;
	}, [locationsData?.by_material_length, selectedLengths]);

	// Consumed capacity per location (sum of consumption of all lengths currently assigned there in this session)
	// For INDIVIDUAL_SLOTS the limiting metric is `bars` (each assigned length consumes one slot).
	const consumedPerLocation = useMemo(() => {
		const consumed: Record<string, { weight: number; volume: number; width: number; height: number; bars: number }> = {};
		selectedLengths.forEach((len: any) => {
			const locId = locationByLengthId[String(len.id)];
			if (!locId || locId === NO_LOCATION_VALUE) return;
			const key = String(locId);
			const c = consumptionByLengthId[String(len.id)];
			if (!c) return;
			if (!consumed[key]) consumed[key] = { weight: 0, volume: 0, width: 0, height: 0, bars: 0 };
			consumed[key].weight += c.weight_per_bar;
			consumed[key].volume += c.volume_per_bar;
			consumed[key].width += c.width_per_bar;
			consumed[key].height += c.height_per_bar;
			consumed[key].bars += 1;
		});
		return consumed;
	}, [selectedLengths, locationByLengthId, consumptionByLengthId]);

	type LocationOption = {
		value: string;
		label: string;
		disabled?: boolean;
		isCurrent?: boolean;
		searchText?: string;
	};

	// Per–material-length options: candidate locations (disabled when no availability or doesn't fit)
	const optionsByLengthId = useMemo((): Record<string, LocationOption[]> => {
		const byLengthId: Record<string, LocationOption[]> = {};
		selectedLengths.forEach((len: any) => {
			byLengthId[String(len.id)] = [];
		});
		const byMaterialLength = locationsData?.by_material_length ?? [];
		const locIdToLabel = new Map<string, string>();
		const locIdToType = new Map<string, string>();
		byMaterialLength.forEach((entry: any) => {
			(entry.locations ?? []).forEach((loc: any) => {
				if (loc) {
					const id = String(loc.id);
					if (!locIdToLabel.has(id)) locIdToLabel.set(id, locLabel(loc, id));
					if (!locIdToType.has(id)) locIdToType.set(id, loc.type ?? '');
				}
			});
		});
		selectedLengths.forEach((len: any) => {
			const loc = len.inventory_location;
			if (loc != null && typeof loc === 'object') {
				const id = String(loc.id ?? loc);
				if (id && !locIdToLabel.has(id)) locIdToLabel.set(id, locLabel(loc, id));
				if (id && !locIdToType.has(id)) locIdToType.set(id, (loc as { type?: string }).type ?? '');
			}
		});
		byMaterialLength.forEach((entry: any) => {
			const mid = entry.material_length_id != null ? String(entry.material_length_id) : '';
			const consumption = consumptionByLengthId[mid];
			const locs = entry.locations ?? [];
			const lenConsumption = consumption ?? { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
			const options: LocationOption[] = [];
			locs.forEach((loc: any) => {
				if (loc == null) return;
				const locId = String(loc.id);
				const consumed = consumedPerLocation[locId];
				const cw = consumed?.weight ?? 0;
				const cv = consumed?.volume ?? 0;
				const cwd = consumed?.width ?? 0;
				const ch = consumed?.height ?? 0;
				const cBars = consumed?.bars ?? 0;
				const baseAvailW = toNum(loc.available_weight);
				const baseAvailV = toNum(loc.available_volume);
				const baseAvailWd = toNum(loc.available_width);
				const baseAvailH = toNum(loc.available_height);
				const baseAvailBars = toNum(loc.available_bars);
				const isCurrentBarAssignedHere = locationByLengthId[String(mid)] === locId;
				const addBack = isCurrentBarAssignedHere ? lenConsumption : { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
				const addBackBars = isCurrentBarAssignedHere ? 1 : 0;
				const effectiveAvailable = {
					weight: Math.max(0, baseAvailW - cw + addBack.weight_per_bar),
					volume: Math.max(0, baseAvailV - cv + addBack.volume_per_bar),
					width: Math.max(0, baseAvailWd - cwd + addBack.width_per_bar),
					height: Math.max(0, baseAvailH - ch + addBack.height_per_bar),
					bars: Math.max(0, baseAvailBars - cBars + addBackBars),
				};
			const fits = barFitsInLocation(loc, lenConsumption, effectiveAvailable);
			const noAvailability = hasNoAvailability(loc.type ?? '', effectiveAvailable);
				options.push({
					value: locId,
					label: locLabel(loc, locId),
					searchText: locationSearchText(loc?.display_name, formatLocation) || undefined,
					disabled: !fits || noAvailability,
					isCurrent: Boolean(loc.is_current_location),
				});
			});
			// Only show candidate locations from API; do not add selected or record location if not in candidates
			const seen = new Set<string>();
			byLengthId[mid] = options.filter((o) => {
				if (seen.has(o.value)) return false;
				seen.add(o.value);
				return true;
			});
		});
		// Consumed bars (New Length = 0) hold no physical location: offer only "No Location Assigned".
		selectedLengths.forEach((len: any) => {
			if (rowIsConsumed(len)) {
				byLengthId[String(len.id)] = [{ value: NO_LOCATION_VALUE, label: NO_LOCATION_LABEL }];
			}
		});
		return byLengthId;
	}, [locationsData?.by_material_length, selectedLengths, locationByLengthId, consumptionByLengthId, consumedPerLocation, debouncedNewLengthByKey, formatLocation]);

	const optionValueSetByLengthId = useMemo(() => {
		const out: Record<string, Set<string>> = {};
		Object.keys(optionsByLengthId).forEach((mid) => {
			out[mid] = new Set(optionsByLengthId[mid].map((o) => o.value));
		});
		return out;
	}, [optionsByLengthId]);

	const enabledOptionValueSetByLengthId = useMemo(() => {
		const out: Record<string, Set<string>> = {};
		Object.keys(optionsByLengthId).forEach((mid) => {
			out[mid] = new Set(
				optionsByLengthId[mid].filter((o) => !o.disabled).map((o) => o.value)
			);
		});
		return out;
	}, [optionsByLengthId]);

	// When new length changes: keep selected location only if it's in the new candidate list; otherwise clear it.
	// For consumed bars (New Length = 0) auto-select "No Location Assigned" (the only valid option).
	useEffect(() => {
		if (!open) return;
		setLocationByLengthId((prev) => {
			let changed = false;
			const next = { ...prev };
			selectedLengths.forEach((record: any) => {
				const mid = String(record.id);
				if (rowIsConsumed(record)) {
					if (prev[mid] !== NO_LOCATION_VALUE) {
						next[mid] = NO_LOCATION_VALUE;
						changed = true;
					}
					return;
				}
				if (!rowHasNewLength(record)) return;
				const currentLocId = prev[mid];
				if (currentLocId == null || currentLocId === '') return;
				const candidateSet = optionValueSetByLengthId[mid];
				if (candidateSet?.has(String(currentLocId))) return;
				delete next[mid];
				changed = true;
			});
			return changed ? next : prev;
		});
	}, [open, selectedLengths, debouncedNewLengthByKey, optionValueSetByLengthId]);

	const locationMetaById = useMemo(() => {
		const map = new Map<string, { type: string; available_weight: number; available_volume: number; available_width: number; available_height: number; available_bars: number }>();
		(locationsData?.by_material_length ?? []).forEach((entry: any) => {
			(entry.locations ?? []).forEach((loc: any) => {
				if (loc == null || map.has(String(loc.id))) return;
				map.set(String(loc.id), {
					type: loc.type ?? '',
					available_weight: toNum(loc.available_weight),
					available_volume: toNum(loc.available_volume),
					available_width: toNum(loc.available_width),
					available_height: toNum(loc.available_height),
					available_bars: toNum(loc.available_bars),
				});
			});
		});
		return map;
	}, [locationsData?.by_material_length]);

	const availableLabelByLengthId = useMemo(() => {
		const out: Record<string, string> = {};
		selectedLengths.forEach((len: any) => {
			const mid = String(len.id);
			const selectedLocId = locationByLengthId[mid];
			if (!selectedLocId) {
				out[mid] = '—';
				return;
			}
			const meta = locationMetaById.get(selectedLocId);
			const consumed = consumedPerLocation[selectedLocId];
			const cw = consumed?.weight ?? 0;
			const cv = consumed?.volume ?? 0;
			const cwd = consumed?.width ?? 0;
			const ch = consumed?.height ?? 0;
			const cBars = consumed?.bars ?? 0;
			const effectiveAvailable = {
				weight: Math.max(0, (meta?.available_weight ?? 0) - cw),
				volume: Math.max(0, (meta?.available_volume ?? 0) - cv),
				width: Math.max(0, (meta?.available_width ?? 0) - cwd),
				height: Math.max(0, (meta?.available_height ?? 0) - ch),
				bars: Math.max(0, (meta?.available_bars ?? 0) - cBars),
			};
			const locType = meta?.type ?? '';
			out[mid] = getAvailableColumnLabel(locType, effectiveAvailable);
		});
		return out;
	}, [selectedLengths, locationByLengthId, locationMetaById, consumedPerLocation]);

	const locationDisplayById = useMemo(() => {
		const map = new Map<string, string>();
		(locationsData?.by_material_length ?? []).forEach((entry: any) => {
			(entry.locations ?? []).forEach((loc: any) => {
				if (loc == null) return;
				map.set(String(loc.id), formatLocation(loc.display_name) || loc.code || loc.id);
			});
		});
		selectedLengths.forEach((len: any) => {
			const loc = len.inventory_location;
			const id =
				loc != null && typeof loc === 'object'
					? loc.id ?? loc
					: loc != null && loc !== ''
						? String(loc)
						: null;
			if (!id || map.has(String(id))) return;
			const label =
				(loc != null && typeof loc === 'object' && (formatLocation(loc.display_name) || loc.code)) ||
				(len.inventory_location_display_name != null
					? formatLocation(len.inventory_location_display_name)
					: len.material_shelf_display_name);
			if (label) map.set(String(id), label);
		});
		return map;
	}, [locationsData?.by_material_length, selectedLengths, formatLocation]);

	// Rows without new length keep current location; only rows with new length must have a selected location
	const allHaveLocation = selectedLengths.every((len: any) => {
		if (!rowHasNewLength(len)) return true;
		const mid = String(len.id);
		const id = locationByLengthId[mid];
		const enabledSet = enabledOptionValueSetByLengthId[mid];
		return id != null && id !== '' && enabledSet?.has(String(id));
	});

	const LENGTH_EPSILON = 0.001;

	// Remaining reserved length for a bar (sum of reservation amounts minus already cut)
	const getRemainingReservedLength = (record: any): number => {
		const reservations = record?.material_reservations ?? [];
		return reservations.reduce((sum: number, res: any) => {
			if (res.material_cut_status === 'Cut') return sum;
			const reserved = Number(res.reserved_length ?? 0);
			const cutPerPiece = Number(res.material_cut_length ?? 0) + Number(res.saw_kerf ?? 0);
			const alreadyCut = cutPerPiece * (res.actual_cut_quantity ?? 0);
			return sum + Math.max(0, reserved - alreadyCut);
		}, 0);
	};

	// Rows where new length is less than remaining reserved (conflict with reservations)
	const reservationConflictRows = useMemo(() => {
		const conflicts: Array<{ record: any; newLength: number; remainingReserved: number }> = [];
		selectedLengths.forEach((record: any) => {
			const key = record.id?.toString() ?? record.friendly_id;
			const newLen = newLengthByKey[key];
			if (newLen == null || !Number.isFinite(Number(newLen))) return;
			const newLength = Number(newLen);
			const remainingReserved = getRemainingReservedLength(record);
			if (remainingReserved <= 0) return;
			if (newLength < remainingReserved - LENGTH_EPSILON) {
				conflicts.push({ record, newLength, remainingReserved });
			}
		});
		return conflicts;
	}, [selectedLengths, newLengthByKey]);

	// Difference = Current Length - New Length (3 decimals to match backend/material length precision)
	const getDifference = (record: any) => {
		const current = Number(record.current_length);
		const key = record.id?.toString() ?? record.friendly_id;
		const newLen = newLengthByKey[key];
		if (newLen == null || newLen === undefined) return null;
		return Number((current - newLen).toFixed(3));
	};

	const isJobRelatedCost = (cost: string | undefined | null) =>
		cost != null &&
		(cost.toLowerCase().includes('job_related') || cost.toLowerCase().includes('job related'));

	// When user selects an associated job whose production method differs from the current
	// attribute cost (e.g. cost was "Swiss Job Related" but they filtered Fastlane in the modal
	// and chose a Fastlane job), sync attribute_cost to match the selected job.
	const attributeCostForProductionMethod = (method: string | undefined | null): AttributeCostValue | undefined => {
		if (!method || typeof method !== 'string') return undefined;
		const m = method.trim().toLowerCase();
		if (m === 'fastlane') return ATTRIBUTE_COST.FASTLANE_JOB_RELATED;
		if (m === 'swiss') return ATTRIBUTE_COST.SWISS_JOB_RELATED;
		return undefined;
	};

	function validateAssignmentsFit(): boolean {
		for (const locId of Object.keys(consumedPerLocation)) {
			const consumed = consumedPerLocation[locId];
			const meta = locationMetaById.get(locId);
			if (!meta) continue;
			const { type } = meta;
			if (type === 'SHARED_SHELVES' && meta.available_weight > 0) {
				if (consumed.weight > meta.available_weight) return false;
			} else if (type === 'SHARED_BINS' && meta.available_volume > 0) {
				if (consumed.volume > meta.available_volume) return false;
			} else if (type === 'INDIVIDUAL_SLOTS') {
				if (meta.available_bars > 0 && consumed.bars > meta.available_bars) return false;
			}
		}
		return true;
	}

	// Reset form state when modal opens; default New Length to current length so Location and Attribute Cost stay disabled until user changes it.
	// Pre-seed location with each record's current location so it shows as default and stays selected when user types a new length (if still a candidate).
	useEffect(() => {
		if (open) {
			const defaultNewLengths: Record<string, number> = {};
			const defaultLocations: Record<string, string> = {};
			selectedLengths.forEach((record: any) => {
				const key = record.id?.toString() ?? record.friendly_id;
				const mid = String(record.id);
				const current = Number(record.current_length);
				defaultNewLengths[key] = Number.isFinite(current) ? current : 0;
				const locId =
					record.inventory_location?.id ??
					record.inventory_location ??
					record.material_shelf?.id ??
					record.material_shelf;
				if (locId != null && locId !== '') {
					defaultLocations[mid] = String(locId);
				}
			});
			setNewLengthByKey(defaultNewLengths);
			setDebouncedNewLengthByKey(defaultNewLengths);
			setAttributeCostByKey({});
			setAssociatedJobByKey({});
			setLocationByLengthId(defaultLocations);
			setShowLocationSummary(false);
			setOperatorPinOpen(false);
			setShowErrors(false);
		}
	}, [open, selectedLengths]);

	// Set initial location selection when modal opens and locations data is first ready (only fill in missing)
	useEffect(() => {
		if (!open || !selectedLengths.length || !locationsData?.by_material_length?.length) return;
		setLocationByLengthId((prev) => {
			const next = { ...prev };
			let changed = false;
			selectedLengths.forEach((len: any) => {
				const mid = String(len.id);
				if (next[mid] != null && next[mid] !== '') return; // keep user selection
				const validIds = initialValidOptionIdsByLengthId[mid];
				if (!validIds) return;
				const locId =
					len.inventory_location?.id ??
					len.inventory_location ??
					len.material_shelf?.id ??
					len.material_shelf;
				const key = locId != null && locId !== '' ? String(locId) : null;
				if (key && validIds.has(key)) {
					next[mid] = key;
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [open, selectedLengths, initialValidOptionIdsByLengthId, locationsData?.by_material_length]);

	const updateNewLength = (record: any, value: number | null | undefined) => {
		const key = record.id?.toString() ?? record.friendly_id;
		const current = Number(record.current_length);
		const oldNewLen = newLengthByKey[key];
		const newNewLen = value ?? undefined;
		const oldDiff =
			oldNewLen != null ? Number((current - Number(oldNewLen)).toFixed(3)) : null;
		const newDiff =
			newNewLen != null ? Number((current - Number(newNewLen)).toFixed(3)) : null;
		const signFlipped =
			oldDiff != null && newDiff != null && (oldDiff < 0) !== (newDiff < 0);
		const differenceCleared = newDiff === 0 || newDiff == null;
		if (signFlipped || differenceCleared) {
			setAttributeCostByKey((prev) => ({ ...prev, [key]: undefined }));
			setAssociatedJobByKey((prev) => ({ ...prev, [key]: undefined }));
		}
		setNewLengthByKey((prev) => ({
			...prev,
			[key]: newNewLen,
		}));
		// Keep current location; effect below will clear it if it's not in the new candidate list
	};

	const handleLocationChange = (lengthId: string, locationId: string) => {
		setLocationByLengthId((prev) => ({
			...prev,
			[String(lengthId)]: String(locationId),
		}));
	};

	const getMissingFields = () => {
		const missing: string[] = [];

		selectedLengths.forEach((record) => {
			const label = record.friendly_id ?? 'selected length';
			const errors = getRowFieldErrors(record);
			if (errors.newLength) missing.push(`${label}: ${errors.newLength}`);
			if (errors.location) missing.push(`${label}: ${errors.location}`);
			if (errors.attributeCost) missing.push(`${label}: ${errors.attributeCost}`);
			if (errors.associatedJob) missing.push(`${label}: ${errors.associatedJob}`);
		});

		return missing;
	};

	const getRowFieldErrors = (record: any) => {
		const key = record.id?.toString() ?? record.friendly_id;
		const mid = String(record.id);
		const diff = getDifference(record);
		const hasLengthChange = diff != null && diff !== 0;

		if (!hasLengthChange) {
			return {
				newLength: undefined,
				location: undefined,
				attributeCost: undefined,
				associatedJob: undefined,
			};
		}

		const newLen = newLengthByKey[key];
		const enabledSet = enabledOptionValueSetByLengthId[mid];
		const locationId = locationByLengthId[mid];
		const cost = attributeCostByKey[key];

		return {
			newLength:
				newLen == null || !Number.isFinite(Number(newLen))
					? 'Enter a new length.'
					: undefined,
			location:
				!locationId || !enabledSet?.has(String(locationId))
					? 'Select a valid location.'
					: undefined,
			attributeCost: !cost?.trim?.() ? 'Select an attribute cost.' : undefined,
			associatedJob:
				isJobRelatedCost(cost) && !associatedJobByKey[key]?.id
					? 'Select an associated job.'
					: undefined,
		};
	};

	const renderInlineError = (error?: string) =>
		showErrors && error ? (
			<Typography.Text type="danger" style={{ display: 'block', marginTop: 4 }}>
				{error}
			</Typography.Text>
		) : null;

	const buildModifications = () =>
		selectedLengths.map((record) => {
			const key = record.id?.toString() ?? record.friendly_id;
			const mid = String(record.id);
			const difference = getDifference(record);
			const locationId =
				locationByLengthId[mid] ??
				record.inventory_location?.id ??
				record.inventory_location ??
				record.material_shelf?.id ??
				record.material_shelf;
			// Consumed bars ("No Location Assigned") clear the physical location on the backend.
			const inventoryLocationId =
				locationId == null || locationId === '' || locationId === NO_LOCATION_VALUE
					? null
					: String(locationId);
			return {
				material_length_id: String(record.id),
				attribute_cost: attributeCostByKey[key],
				new_length: newLengthByKey[key],
				difference: difference != null ? Number(difference) : undefined,
				associated_job_id: associatedJobByKey[key]?.id ?? null,
				inventory_location_id: inventoryLocationId,
			};
		});

	const buildLocationSummaryData = () => {
		const first = selectedLengths[0];
		return {
			materialInfo: getMaterialInfoFromRecord(first),
			lengths: selectedLengths.map((record) => {
				const key = record.id?.toString() ?? record.friendly_id;
				const mid = String(record.id);
				const newLen = newLengthByKey[key];
				const consumed = rowIsConsumed(record);
				const locId =
					locationByLengthId[mid] ??
					record.inventory_location?.id ??
					record.inventory_location;
				const locationName = consumed
					? NO_LOCATION_LABEL
					: locId
						? (locationDisplayById.get(String(locId)) ?? 'Unknown')
						: 'Unknown';
				return {
					id: String(record.id),
					friendlyId: record.friendly_id ?? '',
					location: locationName,
					length: newLen != null ? newLen : Number(record.current_length),
					isScrapped: consumed,
					materialInfo: getMaterialInfoFromRecord(record),
				};
			}),
		};
	};

	/** Step 1: validate form fields, then ask for operator PIN. */
	const handleConfirm = () => {
		const missingFields = getMissingFields();
		if (missingFields.length > 0) {
			setShowErrors(true);
			return;
		}

		if (!allHaveLocation) {
			message.error('Please select a location for all material lengths.');
			return;
		}
		if (!validateAssignmentsFit()) {
			message.error('One or more selected locations would exceed capacity. Please choose different locations.');
			return;
		}
		setOperatorPinOpen(true);
	};

	/** Step 2: PIN resolved — save modifications and show location summary. */
	const handleOperatorConfirm = async (userId: number, _pin: string) => {
		if (isSubmitting) return;
		const modifications = buildModifications();
		setIsSubmitting(true);
		try {
			await Promise.resolve(onConfirm?.(userId, modifications));
			setLocationSummaryData(buildLocationSummaryData());
			setOperatorPinOpen(false);
			setShowLocationSummary(true);
		} catch (err) {
			message.error('Failed to record attribution cost');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSummaryComplete = () => {
		setShowLocationSummary(false);
		onClose();
	};

	// When summary is showing, don't switch to empty state even if refetch cleared selectedLengths
	if (!selectedLengths?.length && !showLocationSummary) {
		return (
			<Modal
				title={
					<Space>
						<span>Modify Lengths</span>
						<BuildingTag name={buildingName} />
					</Space>
				}
				open={open}
				onCancel={onClose}
				footer={null}
				destroyOnClose
				centered
				width={640}
			>
				<Typography.Text type="secondary">No material lengths selected.</Typography.Text>
			</Modal>
		);
	}

	const columns = [
		...(onRemoveLength
			? [
				{
					title: '',
					key: 'remove',
					width: 44,
					fixed: 'left' as const,
					render: (_: unknown, record: any) => (
						<Popconfirm
							title="Remove from list?"
							description="Any New Length, Attribute Cost, or Associated Job entered for this row will be lost."
							okText="Remove"
							okButtonProps={{ danger: true }}
							onConfirm={() => onRemoveLength(String(record.id))}
						>
							<Tooltip title="Remove from list">
								<Button
									type="text"
									danger
									size="small"
									icon={<CloseOutlined />}
									aria-label="Remove from list"
								/>
							</Tooltip>
						</Popconfirm>
					),
				},
			]
			: []),
		{
			title: 'Material Type',
			key: 'material_type',
			width: 140,
			render: (_: unknown, record: any) =>
				record.material_type?.display_name ?? '-',
		},
		{
			title: 'Height',
			key: 'height',
			width: 80,
			render: (_: unknown, record: any) => {
				const val = getDimensionCellValue(record, 'height');
				return val != null ? Number(val).toFixed(3) : '-';
			},
		},
		{
			title: 'Width',
			key: 'width',
			width: 80,
			render: (_: unknown, record: any) => {
				const val = getDimensionCellValue(record, 'width');
				return val != null ? Number(val).toFixed(3) : '-';
			},
		},
		{
			title: 'OD',
			key: 'outer_diameter',
			width: 80,
			render: (_: unknown, record: any) => {
				const val = getDimensionCellValue(record, 'od');
				return val != null ? Number(val).toFixed(3) : '-';
			},
		},
		{
			title: 'ID',
			key: 'inner_diameter',
			width: 80,
			render: (_: unknown, record: any) => {
				const val = getDimensionCellValue(record, 'id');
				return val != null ? Number(val).toFixed(3) : '-';
			},
		},
		{
			title: 'Wall',
			key: 'wall_thickness',
			width: 80,
			render: (_: unknown, record: any) => {
				const val = getDimensionCellValue(record, 'wall');
				return val != null ? Number(val).toFixed(3) : '-';
			},
		},
		{
			title: 'Length ID',
			dataIndex: 'friendly_id',
			key: 'friendly_id',
			width: 90,
		},
		{
			title: 'Current',
			key: 'current_length',
			width: 90,
			render: (_: unknown, record: any) => {
				const val = Number(record.current_length);
				return val != null && Number.isFinite(val) ? `${val.toFixed(1)} in.` : '-';
			},
		},
		{
			title: (
				<>
					New Length <Typography.Text type="danger">*</Typography.Text>
				</>
			),
			key: 'new_length',
			width: 150,
			render: (_: unknown, record: any) => {
				const key = record.id?.toString() ?? record.friendly_id;
				const errors = getRowFieldErrors(record);
				return (
					<>
						<InputNumber
							min={0}
							step={0.001}
							precision={3}
							style={{ width: '100%' }}
							value={newLengthByKey[key]}
							onChange={(val) => updateNewLength(record, val ?? undefined)}
							addonAfter="in."
						/>
						{renderInlineError(errors.newLength)}
					</>
				);
			},
		},
		{
			title: 'Difference',
			key: 'difference',
			width: 95,
			render: (_: unknown, record: any) => {
				const diff = getDifference(record);
				return diff != null ? `${diff} in.` : '—';
			},
		},
		{
			title: (
				<>
					Location <Typography.Text type="danger">*</Typography.Text>
				</>
			),
			key: 'location',
			width: 200,
			render: (_: unknown, record: any) => {
				const mid = String(record.id);
				const hasNewLength = rowHasNewLength(record);
				const rowOptions = optionsByLengthId[mid] ?? [];
				const rowOptionSet = optionValueSetByLengthId[mid];
				const currentLocId =
					record.inventory_location?.id ??
					record.inventory_location ??
					record.material_shelf?.id ??
					record.material_shelf;
				const rawValue =
					locationByLengthId[mid] ??
					(currentLocId != null && currentLocId !== ''
						? String(currentLocId)
						: undefined);
				const value =
					rawValue != null && rowOptionSet?.has(String(rawValue))
						? String(rawValue)
						: undefined;
				const disabled = !hasNewLength || locationsRequesting;
				const placeholder = !hasNewLength
					? 'Enter new length first'
					: locationsRequesting
						? 'Loading...'
						: 'Select location';
				// When there is no length change but the record has a location, show it disabled
				const hasLocationNoChange = !hasNewLength && currentLocId != null && currentLocId !== '';
				const locationName =
					locationDisplayById.get(String(currentLocId))
					?? record.inventory_location?.display_name
					?? record.inventory_location?.code
					?? record.inventory_location_display_name;
				// Legacy MaterialShelf names are not Location labels: shown as saved.
				const resolvedLabel =
					locationName != null
						? formatLocation(locationName)
						: (record.material_shelf?.display_name ?? record.material_shelf_display_name ?? null);
				// Never show raw UUID in disabled field; use fallback when no display name is available
				const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
				const locationLabel =
					resolvedLabel && !isUuid(String(resolvedLabel))
						? String(resolvedLabel)
						: 'Location';
				const displayOptions = hasLocationNoChange
					? [{ value: String(currentLocId), label: locationLabel }]
					: rowOptions;
				const displayValue = hasNewLength ? value : (hasLocationNoChange ? String(currentLocId) : undefined);
				const errors = getRowFieldErrors(record);
				return (
					<>
						<Select
							placeholder={placeholder}
							value={displayValue}
							onChange={(locationId) => handleLocationChange(mid, locationId)}
							options={displayOptions}
							optionRender={(opt) => renderLocationOption(opt.data as LocationOption)}
							style={{ width: '100%' }}
							showSearch
							filterOption={filterLocationOption}
							allowClear={false}
							loading={hasNewLength && locationsRequesting}
							disabled={disabled}
						/>
						{renderInlineError(errors.location)}
					</>
				);
			},
		},
		{
			title: 'Available',
			key: 'available',
			width: 120,
			render: (_: unknown, record: any) => {
				const mid = String(record.id);
				return availableLabelByLengthId[mid] ?? '—';
			},
		},
		{
			title: (
				<>
					Attribute Cost <Typography.Text type="danger">*</Typography.Text>
				</>
			),
			key: 'attribute_cost',
			width: 180,
			render: (_: unknown, record: any) => {
				const key = record.id?.toString() ?? record.friendly_id;
				const difference = getDifference(record);
				const attributeCostDisabled = difference == null || difference === 0;
				const errors = getRowFieldErrors(record);
				return (
					<>
						<Select
							placeholder="Select..."
							options={
								attributeCostDisabled
									? []
									: (difference as number) < 0
										? ATTRIBUTE_COST_OPTIONS_INCREASE
										: ATTRIBUTE_COST_OPTIONS_REDUCTION
							}
							value={attributeCostByKey[key] ?? undefined}
							onChange={(val) => {
								setAttributeCostByKey((prev) => ({
									...prev,
									[key]: val ?? undefined,
								}));
								setAssociatedJobByKey((prev) => ({ ...prev, [key]: undefined }));
							}}
							allowClear
							disabled={attributeCostDisabled}
							style={{ width: '100%' }}
						/>
						{renderInlineError(errors.attributeCost)}
					</>
				);
			},
		},
		{
			title: (
				<>
					Associated Job <Typography.Text type="danger">*</Typography.Text>
				</>
			),
			key: 'associated_job',
			width: 200,
			render: (_: unknown, record: any) => {
				const key = record.id?.toString() ?? record.friendly_id;
				const difference = getDifference(record);
				const attributeCostDisabled = difference == null || difference === 0;
				const cost = attributeCostByKey[key];
				const job = associatedJobByKey[key] ?? null;
				const errors = getRowFieldErrors(record);

				if (attributeCostDisabled || !isJobRelatedCost(cost)) {
					return '—';
				}
				const productionMethod = cost?.toLowerCase().includes('swiss')
					? 'Swiss'
					: cost?.toLowerCase().includes('fastlane')
						? 'Fastlane'
						: undefined;
				return (
					<>
						<AssociatedJobField
							value={job}
							onChange={(newJob) => {
								setAssociatedJobByKey((prev) => ({
									...prev,
									[key]: newJob ?? undefined,
								}));
								// Sync attribute_cost when the selected job's production method differs from
								// the current cost (e.g. user had "Swiss Job Related" but in the modal
								// filtered Fastlane and chose a Fastlane job).
								if (newJob?.partsbadger_production_method && isJobRelatedCost(cost)) {
									const matchingCost = attributeCostForProductionMethod(newJob.partsbadger_production_method);
									if (matchingCost && cost !== matchingCost) {
										setAttributeCostByKey((prev) => ({ ...prev, [key]: matchingCost }));
									}
								}
							}}
							disabled={attributeCostDisabled}
							placeholder="Select Job"
							productionMethodFilter={productionMethod}
						/>
						{renderInlineError(errors.associatedJob)}
					</>
				);
			},
		},
	];

	const renderTabletCards = () => (
		<Space direction="vertical" size="middle" style={{ width: '100%' }}>
			{selectedLengths.map((record) => {
				const key = record.id?.toString() ?? record.friendly_id;
				const mid = String(record.id);
				const diff = getDifference(record);
				const hasNewLength = rowHasNewLength(record);
				const rowOptions = optionsByLengthId[mid] ?? [];
				const rowOptionSet = optionValueSetByLengthId[mid];
				const currentLocId =
					record.inventory_location?.id ??
					record.inventory_location ??
					record.material_shelf?.id ??
					record.material_shelf;
				const rawValue =
					locationByLengthId[mid] ??
					(currentLocId != null && currentLocId !== ''
						? String(currentLocId)
						: undefined);
				const value =
					rawValue != null && rowOptionSet?.has(String(rawValue))
						? String(rawValue)
						: undefined;
				const attributeCostDisabled = diff == null || diff === 0;
				const cost = attributeCostByKey[key];
				const productionMethod = cost?.toLowerCase().includes('swiss')
					? 'Swiss'
					: cost?.toLowerCase().includes('fastlane')
						? 'Fastlane'
						: undefined;
				const errors = getRowFieldErrors(record);
				const renderReadOnlyField = (label: string, value: React.ReactNode) => (
					<div>
						<Typography.Text type="secondary">{label}</Typography.Text>
						<div>{value}</div>
					</div>
				);

				return (
					<Card
						key={record.id}
						size="small"
						title={`${record.friendly_id ?? 'Material Length'} - ${
							record.material_type?.display_name ?? 'Material'
						}`}
						styles={{ body: { padding: 12 } }}
					>
						{onRemoveLength && (
							<div
								style={{
									display: 'flex',
									justifyContent: 'flex-end',
									marginBottom: 12,
								}}
							>
								<Popconfirm
									title="Remove from list?"
									description="Any New Length, Attribute Cost, or Associated Job entered for this row will be lost."
									okText="Remove"
									okButtonProps={{ danger: true }}
									onConfirm={() => onRemoveLength(String(record.id))}
								>
									<Button
										danger
										size="middle"
										icon={<CloseOutlined />}
										aria-label="Remove from list"
									>
										Remove from list
									</Button>
								</Popconfirm>
							</div>
						)}
						<div
							style={{
								display: 'grid',
								gridTemplateColumns:
									'160px repeat(5, 70px) 90px 95px 95px',
								gap: 12,
								alignItems: 'start',
								overflowX: 'auto',
								marginBottom: 12,
							}}
						>
							{renderReadOnlyField(
								'Material Type',
								record.material_type?.display_name ?? '-'
							)}
							{renderReadOnlyField(
								'Height',
								getDimensionCellValue(record, 'height') != null
									? Number(getDimensionCellValue(record, 'height')).toFixed(3)
									: '-'
							)}
							{renderReadOnlyField(
								'Width',
								getDimensionCellValue(record, 'width') != null
									? Number(getDimensionCellValue(record, 'width')).toFixed(3)
									: '-'
							)}
							{renderReadOnlyField(
								'OD',
								getDimensionCellValue(record, 'od') != null
									? Number(getDimensionCellValue(record, 'od')).toFixed(3)
									: '-'
							)}
							{renderReadOnlyField(
								'ID',
								getDimensionCellValue(record, 'id') != null
									? Number(getDimensionCellValue(record, 'id')).toFixed(3)
									: '-'
							)}
							{renderReadOnlyField(
								'Wall',
								getDimensionCellValue(record, 'wall') != null
									? Number(getDimensionCellValue(record, 'wall')).toFixed(3)
									: '-'
							)}
							{renderReadOnlyField('Length ID', record.friendly_id ?? '-')}
							{renderReadOnlyField(
								'Current',
								`${Number(record.current_length || 0).toFixed(1)} in.`
							)}
							{renderReadOnlyField(
								'Difference',
								diff != null ? `${diff} in.` : '—'
							)}
						</div>

						<div
							style={{
								display: 'grid',
								gridTemplateColumns: '150px 220px 180px 220px',
								gap: 12,
								alignItems: 'start',
								overflowX: 'auto',
							}}
						>
							<div>
								<Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
									New Length <Typography.Text type="danger">*</Typography.Text>
								</Typography.Text>
								<InputNumber
									min={0}
									step={0.001}
									precision={3}
									style={{ width: '100%' }}
									size="large"
									value={newLengthByKey[key]}
									onChange={(val) => updateNewLength(record, val ?? undefined)}
									addonAfter="in."
								/>
								{renderInlineError(errors.newLength)}
							</div>
							<div>
								<Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
									Location <Typography.Text type="danger">*</Typography.Text>
								</Typography.Text>
								<Select
									placeholder={
										!hasNewLength
											? 'Enter new length first'
											: locationsRequesting
												? 'Loading...'
												: 'Select location'
									}
									value={value}
									onChange={(locationId) => handleLocationChange(mid, locationId)}
									options={rowOptions}
									optionRender={(opt) => renderLocationOption(opt.data as LocationOption)}
									style={{ width: '100%' }}
									size="large"
									showSearch
									filterOption={filterLocationOption}
									allowClear={false}
									loading={hasNewLength && locationsRequesting}
									disabled={!hasNewLength || locationsRequesting}
								/>
								{renderInlineError(errors.location)}
								<Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
									Available: {availableLabelByLengthId[mid] ?? '—'}
								</Typography.Text>
							</div>
							<div>
								<Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
									Attribute Cost <Typography.Text type="danger">*</Typography.Text>
								</Typography.Text>
								<Select
									placeholder="Select attribute cost"
									options={
										attributeCostDisabled
											? []
											: (diff as number) < 0
												? ATTRIBUTE_COST_OPTIONS_INCREASE
												: ATTRIBUTE_COST_OPTIONS_REDUCTION
									}
									value={cost ?? undefined}
									onChange={(val) => {
										setAttributeCostByKey((prev) => ({
											...prev,
											[key]: val ?? undefined,
										}));
										setAssociatedJobByKey((prev) => ({ ...prev, [key]: undefined }));
									}}
									allowClear
									disabled={attributeCostDisabled}
									style={{ width: '100%' }}
									size="large"
								/>
								{renderInlineError(errors.attributeCost)}
							</div>
							<div>
								<Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
									Associated Job <Typography.Text type="danger">*</Typography.Text>
								</Typography.Text>
								{!attributeCostDisabled && isJobRelatedCost(cost) ? (
									<>
									<AssociatedJobField
										value={associatedJobByKey[key] ?? null}
										onChange={(newJob) => {
											setAssociatedJobByKey((prev) => ({
												...prev,
												[key]: newJob ?? undefined,
											}));
											if (
												newJob?.partsbadger_production_method &&
												isJobRelatedCost(cost)
											) {
												const matchingCost = attributeCostForProductionMethod(
													newJob.partsbadger_production_method
												);
												if (matchingCost && cost !== matchingCost) {
													setAttributeCostByKey((prev) => ({
														...prev,
														[key]: matchingCost,
													}));
												}
											}
										}}
										disabled={attributeCostDisabled}
										placeholder="Select Job"
										productionMethodFilter={productionMethod}
									/>
									{renderInlineError(errors.associatedJob)}
									</>
								) : (
									<Typography.Text type="secondary">Not required</Typography.Text>
								)}
							</div>
						</div>
					</Card>
				);
			})}
		</Space>
	);

	return (
		<>
			<Modal
				title={
					<Space>
						<span>Modify Lengths</span>
						<BuildingTag name={buildingName} />
					</Space>
				}
				open={open}
				onCancel={() => {
					if (showLocationSummary) return;
					setOperatorPinOpen(false);
					onClose();
				}}
				maskClosable={false}
				footer={showLocationSummary ? null : (
					<Space wrap align="center" size={isTablet ? 'middle' : 'small'}>
						<Button
							size={isTablet ? 'large' : 'middle'}
							onClick={() => {
								setOperatorPinOpen(false);
								onClose();
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							size={isTablet ? 'large' : 'middle'}
							onClick={handleConfirm}
							loading={isSubmitting}
							disabled={isSubmitting}
						>
							Confirm
						</Button>
					</Space>
				)}
				destroyOnClose
				centered
				width={isTablet ? '92vw' : 1920}
				styles={{ body: { maxHeight: isTablet ? '70vh' : '90vh', overflowY: 'auto' } }}
			>
				{!showLocationSummary ? (
				<Space direction="vertical" size="middle" style={{ width: '100%' }}>
					{reservationConflictRows.length > 0 && (
						<Card style={{ marginBottom: '1.5rem', background: '#fff1f0', borderColor: '#ffa39e' }} size="small">
							<div style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#cf1322' }}>
								<DeleteOutlined />
								<Typography.Text strong type="danger">
									{reservationConflictRows.length === 1
										? `Warning: New Length (${reservationConflictRows[0].newLength.toFixed(3)}") is less than Remaining Reserved (${reservationConflictRows[0].remainingReserved.toFixed(3)}") for Length ID ${reservationConflictRows[0].record.friendly_id ?? reservationConflictRows[0].record.id}.`
										: `Warning: The following bars have New Length less than Remaining Reserved:`}
								</Typography.Text>
							</div>
							{reservationConflictRows.length > 1 && (
								<ul
									style={{
										margin: '8px 0 0 0',
										paddingLeft: '20px',
										display: 'grid',
										gridTemplateColumns: '1fr',
										gap: '4px 24px',
										listStyleType: 'disc',
										listStylePosition: 'inside',
										color: '#cf1322',
									}}
								>
									{reservationConflictRows.map(({ record, newLength, remainingReserved }) => (
										<li key={record.id}>
											<Typography.Text type="danger" style={{ fontSize: '13px' }}>
												Length ID {record.friendly_id ?? record.id}: New Length ({newLength.toFixed(3)}&quot;) &lt; Remaining Reserved ({remainingReserved.toFixed(3)}&quot;)
											</Typography.Text>
										</li>
									))}
								</ul>
							)}
							<Typography.Text type="danger" style={{ fontSize: '13px', display: 'block', marginTop: 8 }}>
								Proceeding will result in a negative Available Length for {reservationConflictRows.length === 1 ? 'this bar' : 'these bars'}.
							</Typography.Text>
						</Card>
					)}
					{isTablet ? (
						renderTabletCards()
					) : (
						<Table
							columns={columns}
							dataSource={selectedLengths}
							rowKey="id"
							pagination={false}
							scroll={{ x: 'max-content', y: 720 }}
							size="small"
						/>
					)}
				</Space>
				) : null}

				{showLocationSummary && (
					<MaterialLocationsSummaryModal
						visible={showLocationSummary}
						onComplete={handleSummaryComplete}
						materialInfo={locationSummaryData.materialInfo}
						lengths={locationSummaryData.lengths}
						title="Material Locations Summary"
						source="receiving"
					/>
				)}
			</Modal>

			<OperatorPinModal
				open={open && operatorPinOpen && !showLocationSummary}
				title="Identify operator"
				description="Enter the operator PIN to confirm these length modifications."
				label="Operator"
				onCancel={() => setOperatorPinOpen(false)}
				onConfirm={handleOperatorConfirm}
			/>
		</>
	);
};

export default ModifyLengthsModal;
